import { Dialog, DialogContent } from '@mui/material';
import React, {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import DaumPostcode, { Address as DaumAddress } from 'react-daum-postcode';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RequestEditAddressBody } from '../../../../../shared/api/hscan/address';
import addressIcon from '../../../assets/icons/icon_address.svg';
import editIcon from '../../../assets/icons/icon_edit.svg';
import { WindowSize, WindowSizeContext } from '../../../hooks/windowSizeHook';
import { ColoredIcon, IconButton } from '../Icon';

import { MultiRowSlotWrapper, SlotWrapperProps } from './SlotWrapper';
import { StyledTextField } from './TextFieldSlot';
import { useEnrollSlotRef } from './hooks';
import { SlotRef } from './types';

const PostalCodeTextField = styled(StyledTextField)`
  padding: 9px 0px;
  ${p => (!p.$fullWidth ? 'max-width: 90px;' : '')}
`;

const BaseAddressTextField = styled(StyledTextField)`
  padding-right: 4px;
  padding-left: 4px;
  min-width: 250px;
  direction: rtl; // 너무 길 때 뒤부터 보여주기 위해
]
  ${p => p.theme?.isMobile && 'min-width: min(250px, 100%);'}
`;

const SaveButton = styled.button`
  flex-shrink: 0;
  background-color: #2e5be0;
  color: white;
  font-weight: bold;
  text-align: center;
  padding: 10px 20px;
  border-radius: 5px;
  border-style: none;
  min-width: 65px;
`;

export interface AddressTextFieldProps
  extends Omit<SlotWrapperProps, 'mergedCell' | 'icon'> {
  value?: RequestEditAddressBody;
  onChange?: (value: RequestEditAddressBody) => void;
  onEdit?: () => void;
  onSave?: (value: RequestEditAddressBody) => void;
  readOnly?: boolean;
  showSaveButton?: boolean;
}
export const AddressTextField = forwardRef(
  (
    {
      value,
      onChange,
      onEdit,
      onSave,
      readOnly,
      showSaveButton,
      ...slotWrapperProps
    }: AddressTextFieldProps,
    ref: Ref<SlotRef>,
  ) => {
    const wrapperRef = useRef(null);
    const postalCodeRef = useRef<HTMLInputElement>(null);
    const baseAddressRef = useRef<HTMLInputElement>(null);
    const detailAddressRef = useRef<HTMLInputElement>(null);
    useEnrollSlotRef({
      ref,
      wrapperRef,
      inputs: [
        postalCodeRef.current,
        baseAddressRef.current,
        detailAddressRef.current,
      ],
    });

    const { t } = useTranslation('translation', {
      keyPrefix: 'common.textField.address',
    });

    const [open, setOpen] = useState(false);
    const [postalCode, setPostalCode] = useState(value?.postalCode ?? '');
    const [baseAddress, setBaseAddress] = useState(value?.baseAddress ?? '');
    const [detailAddress, setDetailAddress] = useState(
      value?.detailAddress ?? '',
    );

    const windowSize = useContext(WindowSizeContext);
    const isMobile = windowSize === WindowSize.MOBILE;

    useEffect(() => {
      if (
        value &&
        !postalCode.length &&
        !baseAddress.length &&
        !detailAddress.length
      ) {
        setPostalCode(value.postalCode);
        setBaseAddress(value.baseAddress);
        setDetailAddress(value.detailAddress);
      }
    }, [baseAddress.length, detailAddress.length, postalCode.length, value]);

    useEffect(() => {
      if (!showSaveButton) {
        onChange?.({
          postalCode,
          baseAddress,
          detailAddress,
        });
      }
    }, [detailAddress, onChange, showSaveButton, postalCode, baseAddress]);
    const onDaumComplete = (data: DaumAddress) => {
      setPostalCode(data.zonecode);
      setBaseAddress(data.address);
      setDetailAddress('');
      setOpen(false);
      onEdit?.();
      detailAddressRef.current?.focus();
    };

    return (
      <>
        <MultiRowSlotWrapper
          {...slotWrapperProps}
          icon={
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={addressIcon}
              imageWidth="18px"
              color={'var(--HScan_Light_Blue, #2e5be0)'}
            />
          }
          hideLabelPlaceholder={isMobile}
          mergedCell>
          <>
            <PostalCodeTextField
              value={postalCode}
              placeholder={`${t('postalCode')}`}
              $textAlign="center"
              disabled
              $fullWidth={isMobile}
            />
            {!isMobile && (
              <BaseAddressTextField
                key="baseAddress"
                value={baseAddress}
                placeholder={`${t('baseAddress')}`}
                $textAlign="center"
                disabled
                $fullWidth
              />
            )}
            <IconButton
              disabled={readOnly}
              onClick={() => setOpen(true)}
              imageUrl={editIcon}
              width="24px"
              margin="0px -10px 0px -4px"
              height="24px"
              imageWidth="14px"
              color={'var(--HPACS_Text_Light_Grey, #a3a6ab)'}
            />
          </>
          {isMobile ? (
            <>
              <BaseAddressTextField
                key="baseAddress"
                value={baseAddress}
                placeholder={`${t('baseAddress')}`}
                $textAlign="center"
                disabled
                $fullWidth
              />
            </>
          ) : null}
          <>
            <StyledTextField
              ref={detailAddressRef}
              value={detailAddress}
              placeholder={`${t('detailAddress')}`}
              $textAlign="center"
              readOnly={readOnly}
              onChange={e => setDetailAddress(e.target.value)}
              $fullWidth
            />
            {showSaveButton && (
              <SaveButton
                onClick={() =>
                  onSave?.({ postalCode, baseAddress, detailAddress })
                }>
                {t('save')}
              </SaveButton>
            )}
          </>
        </MultiRowSlotWrapper>
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogContent>
            {React.createElement(DaumPostcode as any, {
              onComplete: onDaumComplete,
              autoClose: false,
            })}
          </DialogContent>
        </Dialog>
      </>
    );
  },
);
